import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Music, Loader2, CheckCircle2, AlertCircle, Wrench, Search, FileAudio, AlertTriangle, Link2, Upload } from 'lucide-react';

const AdminTrackRepair = () => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [deepScanning, setDeepScanning] = useState(false);
  const [linking, setLinking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scan, setScan] = useState(null);
  const [report, setReport] = useState(null);
  const [deepScan, setDeepScan] = useState(null);
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkReport, setLinkReport] = useState(null);
  const [uploadReport, setUploadReport] = useState(null);
  const [msg, setMsg] = useState({ kind: '', text: '' });
  const fileInputRef = useRef(null);

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  });

  const runScan = async () => {
    setScanning(true);
    setMsg({ kind: '', text: '' });
    try {
      const r = await fetch(`${API}/api/admin/track-repair/scan`, { headers: headers() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      setScan(await r.json());
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setScanning(false);
    }
  };

  const runFix = async () => {
    if (!window.confirm('This will rewrite audio_url for every track that currently points to a 30s "_sample.mp3" file. Full tracks are HEAD-checked before any change is saved. Continue?')) return;
    setFixing(true);
    setMsg({ kind: '', text: '' });
    try {
      const r = await fetch(`${API}/api/admin/track-repair/fix-all`, { method: 'POST', headers: headers() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      const data = await r.json();
      setReport(data);
      setMsg({ kind: 'ok', text: `Done — updated ${data.summary.updated}, skipped ${data.summary.skipped}, errors ${data.summary.errors}.` });
      // Refresh scan so the Affected list shrinks to show what remains (should be empty).
      runScan();
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setFixing(false);
    }
  };

  useEffect(() => { runScan(); }, []); // eslint-disable-line

  const runDeepScan = async () => {
    setDeepScanning(true);
    setMsg({ kind: '', text: '' });
    try {
      const r = await fetch(`${API}/api/admin/track-repair/deep-scan`, { headers: headers() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      setDeepScan(await r.json());
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setDeepScanning(false);
    }
  };

  const fmtSize = (kb) => kb == null ? '?' : (kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(1)} MB`);
  const fmtDur = (s) => s == null ? '?' : (s < 60 ? `${s}s` : `${Math.floor(s/60)}m ${s%60}s`);

  const runAutoLinkPreview = async () => {
    setLinking(true);
    setMsg({ kind: '', text: '' });
    setLinkReport(null);
    try {
      const r = await fetch(`${API}/api/admin/track-repair/auto-link-preview`, { headers: headers() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      setLinkPreview(await r.json());
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setLinking(false);
    }
  };

  const runAutoLinkApply = async () => {
    if (!linkPreview || linkPreview.summary.proposals === 0) return;
    if (!window.confirm(`Apply ${linkPreview.summary.proposals} auto-match proposals? Each track's audio_url will be rewired to the full-length file found in your media library.`)) return;
    setApplying(true);
    setMsg({ kind: '', text: '' });
    try {
      const r = await fetch(`${API}/api/admin/track-repair/auto-link-apply`, { method: 'POST', headers: headers() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      const data = await r.json();
      setLinkReport(data);
      setMsg({ kind: 'ok', text: `Linked ${data.summary.applied} tracks. ${data.summary.unmatched} still need a file uploaded.` });
      runAutoLinkPreview();
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setApplying(false);
    }
  };

  const [syncingAlbums, setSyncingAlbums] = useState(false);
  const [albumSyncReport, setAlbumSyncReport] = useState(null);

  const handleManualLink = async (trackId, mediaUrl, title) => {
    try {
      const r = await fetch(`${API}/api/admin/track-repair/manual-link`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ track_id: trackId, media_url: mediaUrl }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      setMsg({ kind: 'ok', text: `Linked "${title}" to the chosen file.` });
      runAutoLinkPreview();
    } catch (err) {
      setMsg({ kind: 'err', text: err.message });
    }
  };

  const runAlbumTrackSync = async () => {
    setSyncingAlbums(true);
    setMsg({ kind: '', text: '' });
    setAlbumSyncReport(null);
    try {
      const r = await fetch(`${API}/api/admin/track-repair/sync-album-track-lists`, {
        method: 'POST', headers: headers(),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      const data = await r.json();
      setAlbumSyncReport(data);
      setMsg({ kind: 'ok', text: `Updated ${data.summary.updated} album(s). ${data.summary.unchanged} already correct.` });
    } catch (err) {
      setMsg({ kind: 'err', text: err.message });
    } finally {
      setSyncingAlbums(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadReport(null);
    setMsg({ kind: '', text: '' });
    const perFile = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await fetch(`${API}/api/admin/track-repair/bulk-upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
          body: fd,
        });
        const data = await r.json().catch(() => ({}));
        perFile.push({ filename: file.name, size: file.size, ok: r.ok, info: data });
      }
      setUploadReport({ files: perFile });
      setMsg({ kind: 'ok', text: `Uploaded ${perFile.filter(f => f.ok).length}/${perFile.length}. Click "Scan & propose links" to re-match.` });
    } catch (err) {
      setMsg({ kind: 'err', text: err.message });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 py-12">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/admin" data-testid="back-to-admin" className="inline-flex items-center text-sm text-slate-400 hover:text-amber-400 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Admin
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Music className="w-7 h-7 text-amber-400" />
          <h1 className="text-3xl font-bold">Track Repair</h1>
        </div>
        <p className="text-slate-400 mb-8">
          Fixes tracks whose download delivers the <strong>30-second preview</strong> instead of the full song. The full master files already exist on your WordPress server — this tool just rewrites the saved URL to point at them.
        </p>

        {msg.text && (
          <div
            data-testid="track-repair-msg"
            className={`mb-6 rounded-xl px-4 py-3 flex items-start gap-2 text-sm ${msg.kind === 'ok' ? 'bg-emerald-900/40 border border-emerald-700 text-emerald-200' : 'bg-rose-900/40 border border-rose-700 text-rose-200'}`}
          >
            {msg.kind === 'ok' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
            <span className="whitespace-pre-wrap">{msg.text}</span>
          </div>
        )}

        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Search className="w-5 h-5 text-amber-400" /> Affected tracks
            </h2>
            <button
              data-testid="rescan-btn"
              onClick={runScan}
              disabled={scanning}
              className="text-sm text-slate-300 hover:text-amber-400"
            >
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rescan'}
            </button>
          </div>
          {scan === null ? (
            <p className="text-slate-500 text-sm">Scanning…</p>
          ) : scan.total_affected === 0 ? (
            <p className="text-emerald-300 text-sm">All good — no tracks are pointing at _sample files.</p>
          ) : (
            <>
              <p className="text-slate-300 text-sm mb-4">
                <strong>{scan.total_affected}</strong> track{scan.total_affected === 1 ? '' : 's'} currently deliver the preview instead of the full track.
              </p>
              <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-800 divide-y divide-slate-800">
                {scan.tracks.map((t) => (
                  <div key={t.id} className="px-4 py-3 text-sm">
                    <div className="font-medium text-slate-100">{t.title} <span className="text-slate-500 font-normal">— {t.artist}</span></div>
                    <div className="text-xs text-rose-300 truncate mt-1">now: {t.audio_url}</div>
                    {t.audio_url_fixed ? (
                      <>
                        <div className="text-xs text-emerald-300 truncate">will become: {t.audio_url_fixed}</div>
                        <div className="text-[10px] text-slate-500 italic">↳ {t.audio_how}</div>
                      </>
                    ) : (
                      <div className="text-xs text-amber-300">⚠ {t.audio_how}</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <button
          data-testid="run-track-fix"
          onClick={runFix}
          disabled={fixing || !scan || scan.total_affected === 0}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-950 font-semibold py-4 transition"
        >
          {fixing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wrench className="w-5 h-5" />}
          {fixing ? 'Repairing…' : 'Repair All Affected Tracks'}
        </button>

        {report && (
          <div className="mt-6 bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-3">Last run report</h3>
            <div className="grid grid-cols-3 gap-3 mb-4 text-center text-sm">
              <div className="rounded-lg bg-emerald-900/30 border border-emerald-800 py-3">
                <div className="text-2xl font-bold text-emerald-300">{report.summary.updated}</div>
                <div className="text-emerald-400">updated</div>
              </div>
              <div className="rounded-lg bg-slate-800/60 border border-slate-700 py-3">
                <div className="text-2xl font-bold text-slate-300">{report.summary.skipped}</div>
                <div className="text-slate-400">skipped</div>
              </div>
              <div className="rounded-lg bg-rose-900/30 border border-rose-800 py-3">
                <div className="text-2xl font-bold text-rose-300">{report.summary.errors}</div>
                <div className="text-rose-400">errors</div>
              </div>
            </div>
            {report.skipped?.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-slate-400">Why some tracks were skipped</summary>
                <ul className="mt-2 space-y-1 text-slate-400">
                  {report.skipped.map((s, i) => (
                    <li key={i}>• <strong>{s.title}</strong>: {s.reason}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {/* DEEP SCAN — HEAD-check every track's actual file size */}
        <div className="mt-10 bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FileAudio className="w-5 h-5 text-amber-400" /> Deep scan: real file sizes
            </h2>
            <button
              data-testid="deep-scan-btn"
              onClick={runDeepScan}
              disabled={deepScanning}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-semibold px-4 py-2"
            >
              {deepScanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {deepScanning ? 'Scanning every track…' : 'Run deep scan'}
            </button>
          </div>
          <p className="text-sm text-slate-400 mb-4">
            Downloads the HEAD of every track&apos;s audio file to measure its actual size on disk. Anything under <strong>500&nbsp;KB</strong> (about 30&nbsp;seconds at normal MP3 bitrate) is flagged as a sample.
          </p>

          {deepScan && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-4 text-center text-sm">
                <div className="rounded-lg bg-emerald-900/30 border border-emerald-800 py-3">
                  <div className="text-2xl font-bold text-emerald-300">{deepScan.summary.ok}</div>
                  <div className="text-emerald-400">full tracks</div>
                </div>
                <div className="rounded-lg bg-rose-900/30 border border-rose-800 py-3">
                  <div className="text-2xl font-bold text-rose-300">{deepScan.summary.samples_detected}</div>
                  <div className="text-rose-400">samples found</div>
                </div>
                <div className="rounded-lg bg-amber-900/30 border border-amber-800 py-3">
                  <div className="text-2xl font-bold text-amber-300">{deepScan.summary.unreachable}</div>
                  <div className="text-amber-400">unreachable</div>
                </div>
                <div className="rounded-lg bg-slate-800/60 border border-slate-700 py-3">
                  <div className="text-2xl font-bold text-slate-300">{deepScan.summary.no_url}</div>
                  <div className="text-slate-400">no audio file</div>
                </div>
              </div>

              {deepScan.samples_detected.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-rose-300 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Tracks currently delivering a sample (need full master uploaded)
                  </h4>
                  <div className="max-h-80 overflow-y-auto rounded-lg border border-rose-900/60 divide-y divide-slate-800">
                    {deepScan.samples_detected.map((t) => (
                      <div key={t.id} className="px-4 py-2 text-sm flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-100 truncate">{t.title}</div>
                          <div className="text-xs text-slate-500 truncate">{t.artist}</div>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <div className="text-rose-300 font-mono text-xs">{fmtSize(t.size_kb)}</div>
                          <div className="text-slate-500 text-xs">~{fmtDur(t.estimated_seconds)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {deepScan.unreachable.length > 0 && (
                <details className="text-sm mb-3">
                  <summary className="cursor-pointer text-amber-300 font-semibold">Unreachable files ({deepScan.unreachable.length})</summary>
                  <ul className="mt-2 space-y-1 text-slate-400 text-xs">
                    {deepScan.unreachable.map((t) => (
                      <li key={t.id}>• <strong className="text-slate-200">{t.title}</strong> — {t.status} — <span className="text-slate-500 break-all">{t.audio_url}</span></li>
                    ))}
                  </ul>
                </details>
              )}

              {deepScan.no_url.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-slate-300 font-semibold">Tracks with no audio file ({deepScan.no_url.length})</summary>
                  <ul className="mt-2 space-y-1 text-slate-400 text-xs">
                    {deepScan.no_url.map((t) => (
                      <li key={t.id}>• <strong className="text-slate-200">{t.title}</strong> — {t.artist}</li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>

        {/* ----------------- AUTO LINK + BULK UPLOAD ----------------- */}
        <div className="mt-10 bg-slate-900/70 border-2 border-amber-700/50 rounded-2xl p-6">
          <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
            <Link2 className="w-5 h-5 text-amber-400" /> Smart repair: auto-link full files already in your media library
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Scans every broken track and tries to find a matching full-length file you&apos;ve already uploaded to the media library. Shows you a preview — nothing changes until you click <strong>Apply</strong>.
          </p>

          <div className="flex flex-wrap gap-3 mb-5">
            <button
              data-testid="scan-propose-btn"
              onClick={runAutoLinkPreview}
              disabled={linking}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-semibold px-4 py-2"
            >
              {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {linking ? 'Scanning…' : 'Scan & propose links'}
            </button>
            {linkPreview && linkPreview.summary.proposals > 0 && (
              <button
                data-testid="apply-links-btn"
                onClick={runAutoLinkApply}
                disabled={applying}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-bold px-4 py-2"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                {applying ? 'Linking…' : `Apply all ${linkPreview.summary.proposals} links`}
              </button>
            )}
          </div>

          {linkPreview && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4 text-center text-sm">
                <div className="rounded-lg bg-emerald-900/30 border border-emerald-800 py-3">
                  <div className="text-2xl font-bold text-emerald-300">{linkPreview.summary.ok_tracks}</div>
                  <div className="text-emerald-400">already OK</div>
                </div>
                <div className="rounded-lg bg-amber-900/30 border border-amber-800 py-3">
                  <div className="text-2xl font-bold text-amber-300">{linkPreview.summary.proposals}</div>
                  <div className="text-amber-400">auto-fixable</div>
                </div>
                <div className="rounded-lg bg-rose-900/30 border border-rose-800 py-3">
                  <div className="text-2xl font-bold text-rose-300">{linkPreview.summary.unmatched}</div>
                  <div className="text-rose-400">need upload</div>
                </div>
              </div>

              {linkPreview.proposals.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-amber-300 mb-2">Proposed matches ({linkPreview.proposals.length})</h4>
                  <div className="max-h-80 overflow-y-auto rounded-lg border border-amber-900/50 divide-y divide-slate-800">
                    {linkPreview.proposals.map((p) => (
                      <div key={p.track_id} className="px-4 py-2 text-xs">
                        <div className="font-semibold text-slate-100">{p.title}</div>
                        <div className="text-rose-300">now: {p.current_audio_url} ({p.current_size_bytes ? `${Math.round(p.current_size_bytes/1024)} KB` : 'unreachable'})</div>
                        <div className="text-emerald-300">will link to: {p.match_filename} ({Math.round(p.match_size_bytes/1024)} KB) <span className="text-slate-500">score {p.match_score}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {linkPreview.unmatched.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-rose-300 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Still unmatched — pick a file manually
                  </h4>
                  <div className="rounded-lg border border-rose-900/60 divide-y divide-slate-800 mb-4">
                    {linkPreview.unmatched.map((t) => (
                      <div key={t.track_id} className="px-4 py-3 text-xs">
                        <div className="font-semibold text-slate-100 mb-1">
                          {t.title} <span className="text-slate-500 font-normal">— {t.artist}</span>
                        </div>
                        {t.suggestions && t.suggestions.length > 0 ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <select
                              data-testid={`manual-pick-${t.track_id}`}
                              defaultValue=""
                              onChange={(e) => e.target.value && handleManualLink(t.track_id, e.target.value, t.title)}
                              className="flex-1 min-w-[280px] bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
                            >
                              <option value="">— Pick the correct file from library —</option>
                              {t.suggestions.map((s, i) => (
                                <option key={i} value={s.url}>
                                  [{s.score}] {s.filename} ({Math.round(s.size / 1024)} KB)
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="text-slate-500">No audio files in library to choose from — upload the master above.</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ----------------- BULK UPLOAD ----------------- */}
        <div className="mt-6 bg-slate-900/70 border-2 border-sky-700/50 rounded-2xl p-6">
          <h2 className="text-xl font-semibold flex items-center gap-2 mb-2">
            <Upload className="w-5 h-5 text-sky-400" /> Bulk upload full-length masters
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Drop one or many MP3/WAV files below. They&apos;ll land in your media library. After upload finishes, click <strong>Scan &amp; propose links</strong> above and the auto-matcher will wire them to the right track automatically — no manual pairing needed.
          </p>
          <input
            ref={fileInputRef}
            data-testid="bulk-upload-input"
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.flac"
            multiple
            onChange={handleBulkUpload}
            disabled={uploading}
            className="block w-full text-sm text-slate-200 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:bg-sky-500 file:text-slate-950 file:font-bold hover:file:bg-sky-400 disabled:opacity-50 cursor-pointer"
          />
          {uploading && (
            <p className="mt-3 text-sm text-sky-300 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading files…
            </p>
          )}
          {uploadReport && (
            <div className="mt-4 text-xs rounded-lg border border-slate-800 divide-y divide-slate-800">
              {uploadReport.files.map((f, i) => (
                <div key={i} className="px-4 py-2 flex items-center justify-between">
                  <span className="text-slate-200 truncate">{f.filename}</span>
                  <span className={f.ok ? 'text-emerald-400' : 'text-rose-400'}>
                    {f.ok ? '✓ uploaded' : '✗ failed'} ({Math.round(f.size / 1024)} KB)
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* ----------------- ALBUM TRACK LIST SYNC ----------------- */}
        <div className="mt-6 bg-slate-900/70 border-2 border-purple-700/50 rounded-2xl p-6">
          <h2 className="text-xl font-semibold flex items-center gap-2 mb-2">
            <Wrench className="w-5 h-5 text-purple-400" /> Sync album track lists
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Fixes albums that show <strong>&quot;0 tracks&quot; on the public page even though admin says they have tracks</strong>. Rebuilds each album&apos;s track list from the tracks that actually point at it.
          </p>
          <button
            data-testid="sync-album-tracks-btn"
            onClick={runAlbumTrackSync}
            disabled={syncingAlbums}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-slate-950 text-sm font-bold px-4 py-2"
          >
            {syncingAlbums ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            {syncingAlbums ? 'Syncing…' : 'Sync every album now'}
          </button>
          {albumSyncReport && (
            <div className="mt-4 text-xs">
              <div className="text-slate-300 mb-2">
                <strong>{albumSyncReport.summary.updated}</strong> albums updated, {albumSyncReport.summary.unchanged} already correct.
              </div>
              {albumSyncReport.updated.length > 0 && (
                <div className="rounded-lg border border-purple-900/50 divide-y divide-slate-800 max-h-64 overflow-y-auto">
                  {albumSyncReport.updated.map((a) => (
                    <div key={a.id} className="px-3 py-2 flex justify-between">
                      <span className="text-slate-200">{a.title}</span>
                      <span className="text-emerald-300 font-mono">{a.before} → {a.after} tracks</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminTrackRepair;
